import { Box, Stack, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import type { LivePerformance } from "@/content/types";
import { MediaImage } from "@/components/common/MediaImage";
import { resolvePublic } from "@/lib/media";
import { register, report, type Subscriber } from "./hoverlessAutoplay";

// IO thresholds: enough crossings that the coordinator gets fresh ratio +
// boundingClientRect.top reports through a scroll without firing on every
// pixel. 0 is included so we know when a tile fully leaves the viewport.
const IO_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];

export function LiveTile({ show }: { show: LivePerformance }) {
  // "Treat as touch" = no hover capability OR coarse pointer. Either flag
  // matches a phone/tablet; matching either also makes Chrome DevTools mobile
  // emulation work (it usually flips at least one of them when a device
  // profile is selected — though "Responsive" mode often doesn't, in which
  // case toggle them via the Rendering panel for testing).
  //
  // useMediaQuery is reactive — rotating a tablet or flipping devtools
  // emulation switches the path without a refresh.
  const noHover = useMediaQuery("(hover: none)", false);
  const coarsePointer = useMediaQuery("(pointer: coarse)", false);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", false);
  const isHoverless = noHover || coarsePointer;

  // Desktop: hover state. Mobile: coordinator-driven active state.
  // The video plays when either is true.
  const [hovering, setHovering] = useState(false);
  const [autoActive, setAutoActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLAnchorElement | null>(null);
  const previewUrl = show.previewKey ? resolvePublic(show.previewKey) : null;

  const playing = hovering || autoActive;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      // muted+inline satisfies iOS Safari's autoplay rules; older versions can
      // still reject — swallow silently.
      void v.play().catch(() => {});
    } else {
      v.pause();
      v.currentTime = 0;
    }
  }, [playing]);

  // Hover-less coordinator wiring. Only mounts an observer when (a) the
  // device has no hover, (b) the user hasn't opted out of motion, and
  // (c) there's actually a preview file to play.
  useEffect(() => {
    if (!isHoverless || reducedMotion || !previewUrl) return;
    const container = containerRef.current;
    if (!container) return;

    const sub: Subscriber = {
      setActive: (active) => setAutoActive(active),
    };
    const unsub = register(sub);

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          report(sub, entry.intersectionRatio, entry.boundingClientRect.top);
        }
      },
      { threshold: IO_THRESHOLDS },
    );
    io.observe(container);

    return () => {
      io.disconnect();
      unsub();
      // Ensure we don't leave a paused-but-armed state if the env flips
      // away from hoverless.
      setAutoActive(false);
    };
  }, [isHoverless, reducedMotion, previewUrl]);

  return (
    <Box
      ref={containerRef}
      component={Link}
      to={`/live/${show.slug}`}
      className="lift-on-hover"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        textDecoration: "none",
        color: "inherit",
        border: "1px solid var(--color-ocean)",
        borderRadius: 12,
        overflow: "hidden",
        display: "block",
      }}
    >
      {show.cover && (
        <Box style={{ position: "relative", aspectRatio: "16 / 9", background: "var(--color-deeper)" }}>
          <MediaImage
            image={show.cover}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            sizes="(min-width: 768px) 33vw, 100vw"
          />
          {previewUrl && (
            <video
              ref={videoRef}
              src={previewUrl}
              muted
              loop
              playsInline
              // Desktop: eager-preload so first hover plays instantly (10
              // tiles on /live × ~360 KB avg ≈ 3.6 MB). Touch: metadata-only,
              // since IO triggers playback as the user scrolls — paying
              // up-front for 10 videos on a phone data plan is hostile.
              preload={isHoverless ? "metadata" : "auto"}
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: playing ? 1 : 0,
                transition: "opacity 180ms ease",
                pointerEvents: "none",
              }}
            />
          )}
        </Box>
      )}
      <Stack gap={2} p="sm">
        <Text fw={600} size="sm">
          {show.title}
        </Text>
        <Text size="xs" c="dimmed">
          {show.venue}
        </Text>
      </Stack>
    </Box>
  );
}
